import React, { useMemo } from 'react';
import {
  Box,
  MenuItem,
  Paper,
  Select,
  Typography,
  FormControl,
  TextField,
} from '@mui/material';
import MUITypography from '../../../Components/MUITypography';
import StorageFactory from '../../../Util/StorageFactory';
import {
  MENU_OPTIONS,
  SESSION_USER_COUNTRY_DATA_KEY,
} from '../../../Util/VMConstants';
import MuiTypographyMandatory from '../../../Components/Custom/MuiTypographyMandatory';
import { handleWheel, toTitleCase } from '../../../Util/UtilHelper';
import { VM_Colors } from '../../../Styles/VMStyles';

function FoodBilling({ bookedEventState = [], updateFoodBillingHandler }) {
  const sessionFactory = new StorageFactory('session');
  const currencySymbol =
    sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';

  const totalFoodCharges = useMemo(() => {
    return bookedEventState.reduce(
      (sum, event) => sum + (parseFloat(event.dining_total_menu_cost) || 0),
      0
    );
  }, [bookedEventState]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <MUITypography variant="h3" sx={{fontWeight: 700}}>Food Billing</MUITypography>

      <Paper
        sx={{
          mt: 1,
          p: 2,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          backgroundColor: '#fff',
          borderRadius: 2,
          boxShadow: '0px 4px 4px rgba(0,0,0,0.09)',
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap={1}>
          <Typography fontWeight={700} sx={{ minWidth: 0 }}>Total Food Charges</Typography>
          <Typography fontWeight={700} color="#16a34a" sx={{ flexShrink: 0 }}>
            {currencySymbol}
            {totalFoodCharges.toLocaleString('en-IN')}
          </Typography>
        </Box>

        <Box sx={{ borderTop: '1px dashed #ccc', my: 2 }} />

        {bookedEventState.map((event, eventIdx) => (
          <Box key={eventIdx} mb={3}>
            <Typography fontWeight={700} mb={1} sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
              Event {eventIdx + 1}: {event.event_name}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 2,
                backgroundColor: VM_Colors.paper_bg,
                p: 2,
                borderRadius: 2,
                minWidth: 0,
                width: '100%',
                maxWidth: '100%',
              }}
            >
              {event.dining_details?.map((menu, menuIdx) => (
                <React.Fragment key={menuIdx}>
                  {/* Menu */}
                  <Box>
                    <MUITypography label="Menu Type" />
                    <FormControl fullWidth>
                      <Select
                        value={menu.menu || ''}
                        onChange={e =>
                          updateFoodBillingHandler(
                            eventIdx,
                            menuIdx,
                            'menu',
                            e.target.value
                          )
                        }
                        displayEmpty
                        sx={{ minWidth: 0, width: '100%', maxWidth: '100%' }}
                      >
                        <MenuItem value="" disabled>
                          Select
                        </MenuItem>
                        {MENU_OPTIONS.map(option => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Plates */}
                  <Box>
                    <MuiTypographyMandatory label="No. of Plates" required />
                    <TextField
                      fullWidth
                      type="number"
                      value={menu.no_of_plates || ''}
                      onChange={e =>
                        updateFoodBillingHandler(
                          eventIdx,
                          menuIdx,
                          'no_of_plates',
                          e.target.value
                        )
                      }
                      placeholder={toTitleCase('No. of Plates')}
                      onWheel={handleWheel}
                      onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      }}
                    />
                  </Box>

                  {/* Cost */}
                  <Box>
                    <MuiTypographyMandatory label="Cost per Plate" required />
                    <TextField
                      fullWidth
                      type="number"
                      value={menu.cost_per_plate || ''}
                      onChange={e =>
                        updateFoodBillingHandler(
                          eventIdx,
                          menuIdx,
                          'cost_per_plate',
                          e.target.value
                        )
                      }
                      placeholder={toTitleCase('Cost per Plate')}
                      onWheel={handleWheel}
                      onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      }}
                    />
                  </Box>

                  {/* Amount */}
                  <Box>
                    <MuiTypographyMandatory label="Amount" />
                    <TextField
                      fullWidth
                      disabled
                      value={menu.total_menu_cost || 0}
                      onWheel={handleWheel}
                      sx={{
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      }}
                    />
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}

export default FoodBilling;